import { ChangeEvent, FC, Fragment, useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { debounce } from 'throttle-debounce';
import dayjs, { Dayjs } from 'dayjs';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Models
import { RootState } from 'app/store';
import ICalendarEvent, { ICalendarEventReminder } from 'app/models/CalendarEvent';
import IOption from 'app/models/Option';
// Types
import CaseStatuses from 'app/types/CaseStatuses';
import ReminderUnitTypes from 'app/types/ReminderUnitTypes';
// Async
import { createCalendarEvent, updateCalendarEvent } from 'app/store/CalendarEvents/CalendarEventsAsync';
import { getInsuranceCasesList } from 'app/store/Cases/Cases.async';
// Selectors
import { selectCalendarEventTypesAsOptions } from 'app/store/CalendarEventTypes/CalendarEventTypesSelectors';
import { selectMyUser, selectUsersAsOptions } from 'app/store/Users/Users.selectors';
import { selectCaseOptions } from 'app/store/Cases/Cases.selectors';
import { selectCurrentAccount } from 'app/store/Accounts/Accounts.selectors';
import { selectTimeZoneAsOptions } from 'app/store/Settings/Settings.selectors';
// MUI
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Autocomplete, Box, Divider, FormHelperText, TextField, Tooltip, Typography } from '@mui/material';
import { DeleteOutlined as DeleteOutlinedIcon } from '@mui/icons-material';
// Components
import Dialog from 'app/components/Dialog';
import { Button, DesktopTimePicker, IconButton, Input, Select, Switch } from 'app/components/Mui';
// Utilities
import { isMaxLength, isMin, isRequired, validateEpisodeTime } from 'app/utilities/Validations';
import { parseTime } from 'app/utilities/DateTime';
import { getBrowserTimezone } from 'app/utilities/Utilities';

interface ISelectInfo {
  start: Dayjs;
  end: Dayjs;
  allDay: boolean;
}

interface IFormData {
  title: string;
  typeId?: any;
  description?: string;
  location?: string;
  start: Dayjs;
  end: Dayjs;
  allDay: boolean;
  timeZone?: string;
  caseId?: any;
  attendeeIds?: any[];
  reminders?: Pick<ICalendarEventReminder, 'durationUnit' | 'durationValue' | 'time'>[];
}

type Props = {
  open: boolean;
  onClose: () => void;
  event?: ICalendarEvent;
  selectInfo?: ISelectInfo;
}

const EventsFormDialog:FC<Props> = ({ open, onClose, event, selectInfo }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const eventTypesOptions = useAppSelector(selectCalendarEventTypesAsOptions);
  const myUser = useAppSelector(selectMyUser);
  const usersOptions = useAppSelector((state:RootState) => selectUsersAsOptions(state, {
    excludeUserId: event ? event.owner.id : myUser?.id
  }));
  const casesOptions = useAppSelector(selectCaseOptions);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const timeZoneOptions = useAppSelector(selectTimeZoneAsOptions) || [];

  const { control, handleSubmit, formState: { errors }, watch, getValues, trigger, clearErrors, setValue } = useForm<IFormData>({
    defaultValues: {
      title: event?.title ?? '',
      typeId: event?.type ?? null,
      description: event?.description ?? '',
      location: event?.location ?? '',
      start: event ? dayjs.utc(event.start) : selectInfo ? dayjs.utc(selectInfo.start) : dayjs().add(1, 'hour').startOf('hour'),
      end: event ? dayjs.utc(event.end).subtract(event.allDay ? 1 : 0, 'days') : selectInfo ? dayjs.utc(selectInfo.end) : dayjs().add(2, 'hour').startOf('hour'),
      allDay: event?.allDay || selectInfo?.allDay || false,
      timeZone: event?.timeZone || getBrowserTimezone(),
      caseId: event?.case ?? null,
      attendeeIds: event?.attendees ?? [],
      reminders: event?.reminders ? event.reminders.map(r => ({ ...r, time: r.time ? parseTime(r.time) : null })) : [],
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'reminders'
  })

  const addReminder = () => {
    append({ durationUnit: ReminderUnitTypes.Hour, durationValue: 2, time: null });
  }

  const allDayWatcher = watch('allDay');
  const startWatcher = watch('start');
  const endWatcher = watch('end');
  const titleWatcher = watch('title');
  const locationWatcher = watch('location');

  const onSubmit = handleSubmit((data:IFormData) => {
    const {
      title, typeId, start, end, allDay, location, timeZone,
      description, caseId, attendeeIds, reminders
    } = data;

    let startDate = dayjs(start);
    let endDate = dayjs(end);
    
    if (allDay) {
      startDate = startDate.hour(0).minute(0).second(0).millisecond(0);
      endDate = endDate.add(1, 'days').hour(0).minute(0).second(0).millisecond(0);
    }

    const nextData:any = {
      title,
      start: `${startDate.format('YYYY-MM-DDTHH:mm:ss.SSS')}Z`,
      end: `${endDate.format('YYYY-MM-DDTHH:mm:ss.SSS')}Z`,
      allDay,
      timeZone
    }

    if (typeId) nextData.typeId = typeId.id; 
    if (location) nextData.location = location;
    if (description) nextData.description = description;
    if (caseId) nextData.caseId = caseId.id;
    if (attendeeIds?.length) nextData.attendeeIds = attendeeIds.map(attendee => attendee.id);
    if (reminders && reminders.length) nextData.reminders = reminders.map((reminder) => {
      const { durationUnit, durationValue, time } = reminder;
      if (reminder.durationUnit === ReminderUnitTypes.Day) {
        return {
          ...reminder,
          time: dayjs(time).format('HH:mm')
        }
      }
      return {durationUnit, durationValue};
    });

    if ( event?.id ){
      asyncUpdateEvent({ eventId: event.id, data: nextData });
    } else {
      asyncCreateEvent(nextData);
    }
  });

  const asyncCreateEvent = async (data:any) => {
    try {
      await dispatch(createCalendarEvent(data)).unwrap();
      onClose();
    } catch(err){}
  }

  const asyncUpdateEvent = async (data:any) => {
    try {
      await dispatch(updateCalendarEvent(data)).unwrap();
      onClose();
    } catch(err){}
  }

  // eslint-disable-next-line
  const debounceCases = useCallback(debounce(500, (search:string) => {
    dispatch(getInsuranceCasesList({ search, limit: 50, status: [CaseStatuses.Archived, CaseStatuses.Open, CaseStatuses.Pending, CaseStatuses.Waiting] }));
  }), []);

  const handleChangeCaseInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    debounceCases(value);
  }

  const normalizeDate = useCallback((date:Dayjs | null) => {
    if (!date) return null;
    return allDayWatcher ? date.startOf('day') : date;
  }, [allDayWatcher]);

  const reminderUnitOptions = useMemo(() => {
    return [
      { id: 'minute', name: t('labels.minutes') },
      { id: 'hour', name: t('labels.hours') },
      { id: 'day', name: t('labels.days') },
    ]
  }, [t]);

  const is12HourFormat = useMemo(() => {
    const timeFormat = currentAccount?.settings?.timeFormat || 'hh:mm a';
    return timeFormat === 'hh:mm a';
  }, [currentAccount]);

  useEffect(() => {
    if (allDayWatcher) return;
    const start = getValues('start');
    const end = getValues('end');
    if (!start || !end) return;
    if (dayjs(start).isSame(dayjs(end))) {
      setValue('end', dayjs(end).add(1, 'hour'));
    }
    // eslint-disable-next-line
  }, [allDayWatcher])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'start' || name === 'end') {
        const start = normalizeDate(getValues('start'));
        const end = normalizeDate(getValues('end'));
  
        const isValid = allDayWatcher
          ? start && end && !start.isAfter(end)
          : start && end && start.isBefore(end);
  
        if (isValid) {
          clearErrors(['start', 'end']);
        } else {
          if (name === 'start') {
            trigger('end');
          } else {
            trigger('start');
          }
        }
      }
    });
  
    return () => subscription.unsubscribe?.();
    // eslint-disable-next-line
  }, [allDayWatcher, normalizeDate]);

  const actions = (
    <Fragment>
      <Button
        name="Cancel event dialog"
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name={`${event ? 'update' : 'create'} event dialog`}
        onClick={onSubmit}
        variant="contained"
        color="primary"
      >{event ? t('buttons.update') : t('buttons.create')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={event ? t('pages.companyCalendar.updateEventFormTitle') : t('pages.companyCalendar.createEventFormTitle')}
      actions={actions}
    >
      <form noValidate>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Title */}
          <Controller
            control={control} name="title"
            rules={{ required: isRequired, maxLength: isMaxLength(100) }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('labels.title')}
                required
                error={Boolean(errors.title)}
                helperText={errors.title?.message || ''}
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <FormHelperText
                        sx={{ p: 0, whiteSpace: 'nowrap' }}
                      >{`${titleWatcher.length} / 100`}</FormHelperText>
                    )
                  }
                }}
              />
            )}
          />

          {/* Type */}
          <Controller
            control={control} name="typeId"
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                fullWidth
                value={value}
                onChange={(_:any, nextValue:any) => onChange(nextValue)}
                options={eventTypesOptions || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('labels.type')}
                    fullWidth
                    margin="normal"
                    size="small"
                  />
                )}
                getOptionLabel={(option:IOption) => option.name}
                isOptionEqualToValue={(option:IOption, value:IOption) => option.id === value.id}
              />
            )}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Start */}
          <Controller
            control={control} name="start"
            rules={{
              required: isRequired,
              validate: (value) => {
                const start = normalizeDate(value);
                const end = normalizeDate(endWatcher);

                if (start && end) {
                  if (allDayWatcher) {
                    if (start.isAfter(end)) {
                      return t('validations.startDateBeforeEndDate');
                    }
                  } else {
                    if (!start.isBefore(end)) {
                      return t('validations.startDateBeforeEndDate');
                    }
                  }
                }

                return true;
              }
            }}
            render={({ field }) => {
              const { value, onChange } = field;

              const diff = dayjs(endWatcher).diff(dayjs(value), 'minutes');

              const handleDateChange = (date: Dayjs | null) => {
                if (!date || !value) return onChange(date);
                const nextDate = value.set('year', date.year()).set('month', date.month()).set('date', date.date());
                let nextEndDate = nextDate.add(diff, 'minutes');
                if (nextEndDate.isBefore(nextDate)) {
                  nextEndDate = nextDate.add(1, allDayWatcher ? 'day' : 'hour');
                }
                setValue('end', nextEndDate);
                onChange(nextDate);
              };

              const handleTimeChange = (time: Dayjs | null) => {
                if (!time || !value) return onChange(time);
                const nextDate = value.set('hour', time.hour()).set('minute', time.minute());
                let nextEndDate = nextDate.add(diff, 'minutes');
                if (nextEndDate.isBefore(nextDate)) {
                  nextEndDate = nextDate.add(1, allDayWatcher ? 'day' : 'hour');
                }
                setValue('end', nextEndDate);
                onChange(nextDate);
              };

              return (
                <Box sx={{ display: 'flex', flexShrink: 0, gap: 2 }}>
                  <DatePicker
                    value={value}
                    onChange={handleDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="start"
                        fullWidth
                        margin="normal"
                        size="small"
                        error={Boolean(errors.start)}
                        helperText={errors.start?.message || ''}
                        sx={{
                          maxWidth: 160,
                          '& .MuiInputAdornment-root': {
                            marginLeft: 0,
                          },
                        }}
                      />
                    )}
                  />
                  {!allDayWatcher && (
                    <TimePicker
                      ampmInClock
                      ampm={is12HourFormat}
                      value={value}
                      onChange={handleTimeChange}
                      minutesStep={5}
                      // showToolbar={false}
                      componentsProps={{
                        actionBar: {
                          actions: ['accept'],
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="start"
                          fullWidth
                          margin="normal"
                          size="small"
                          error={Boolean(errors.start)}
                          sx={{
                            maxWidth: is12HourFormat ? 140 : 110,
                            '& .MuiInputAdornment-root': {
                              marginLeft: 0,
                            },
                          }}
                        />
                      )}
                    />
                  )}
                </Box>
              );
            }}
          />
          <Typography sx={{ mt: 6 }}>-</Typography>
          {/* End */}
          <Controller
            control={control} name="end"
            rules={{
              required: isRequired,
              validate: (value) => {
                const end = normalizeDate(value);
                const start = normalizeDate(startWatcher);
              
                if (start && end) {
                  if (allDayWatcher) {
                    if (end.isBefore(start)) {
                      return t('validations.endDateAfterStartDate');
                    }
                  } else {
                    if (!end.isAfter(start)) {
                      return t('validations.endDateAfterStartDate');
                    }
                  }
                }
              
                return true;
              }
            }}
            render={({ field }) => {
              const { value, onChange } = field;

              const handleDateChange = (date: Dayjs | null) => {
                if (!date || !value) return onChange(date);
                const nextDate = value.set('year', date.year()).set('month', date.month()).set('date', date.date());
                if (startWatcher && dayjs(startWatcher).isAfter(nextDate)) {
                  setValue('start', nextDate.subtract(1, allDayWatcher ? 'day' : 'hour'));
                }
                onChange(nextDate);
              };

              const handleTimeChange = (time: Dayjs | null) => {
                if (!time || !value) return onChange(time);
                const nextDate = value.set('hour', time.hour()).set('minute', time.minute());
                if (startWatcher && dayjs(startWatcher).isAfter(nextDate)) {
                  setValue('start', nextDate.subtract(1, allDayWatcher ? 'day' : 'hour'));
                }
                onChange(nextDate);
              };

              return (
                <Box sx={{ display: 'flex', flexShrink: 0, gap: 2 }}>
                  {(!dayjs(startWatcher).isSame(dayjs(value), 'day') || allDayWatcher) && (
                    <DatePicker
                      value={value}
                      onChange={handleDateChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="end"
                          fullWidth
                          margin="normal"
                          size="small"
                          error={Boolean(errors.end)}
                          helperText={errors.end?.message || ''}
                          sx={{
                            maxWidth: 160,
                            '& .MuiInputAdornment-root': {
                              marginLeft: 0,
                            },
                          }}
                        />
                      )}
                    />
                  )}
                  {!allDayWatcher && (
                    <TimePicker
                      ampmInClock
                      ampm={is12HourFormat}
                      value={value}
                      onChange={handleTimeChange}
                      minutesStep={5}
                      // showToolbar={false}
                      componentsProps={{
                        actionBar: {
                          actions: ['accept'],
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="end"
                          fullWidth
                          margin="normal"
                          size="small"
                          error={Boolean(errors.end)}
                          helperText={
                            !(!dayjs(startWatcher).isSame(dayjs(value), 'day') || allDayWatcher)
                              ? errors.end?.message
                              : ''
                          }
                          sx={{
                            maxWidth: is12HourFormat ? 140 : 110,
                            '& .MuiInputAdornment-root': {
                              marginLeft: 0,
                            },
                          }}
                        />
                      )}
                    />
                  )}
                </Box>
              );
            }}
          />
          {/* Timezone */}
          <Controller
            control={control} name="timeZone"
            render={({ field }) => {
              const timeZoneLabel = timeZoneOptions.find(opt => opt.id === field.value)?.name || '';
              return (
                <Select
                  {...field}
                  label={t('labels.timeZone')}
                  options={timeZoneOptions}
                  size="small"
                  tooltip={timeZoneLabel}
                />
              );
            }}
          />
        </Box>

        {/* All Day */}
        <Controller
          control={control} name="allDay"
          render={({ field }) => (
            <Switch
              {...field}
              name="All day switch"
              label={t('labels.allDay')}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        />

        {/* Location */}
        <Controller
          control={control} name="location"
          rules={{ maxLength: isMaxLength(250) }}
          render={({ field }) => (
            <Input
              {...field}
              size="small"
              label={t('labels.location')}
              error={Boolean(errors.location)}
              helperText={errors.location?.message || ''}
              slotProps={{
                input: {
                  endAdornment: (
                    <FormHelperText
                      sx={{ p: 0, whiteSpace: 'nowrap' }}
                    >{`${locationWatcher?.length} / 250`}</FormHelperText>
                  )
                }
              }}
            />
          )}
        />

        {/* Description */}
        <Controller
          control={control} name="description"
          render={({ field }) => (
            <Input
              {...field}
              size="small"
              label={t('labels.description')}
              multiline
              minRows={2}
              maxRows={4}
            />
          )}
        />

        {/* Attendees */}
        <Controller
          control={control} name="attendeeIds"
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              value={value}
              onChange={(_:any, nextValue:any) => onChange(nextValue)}
              options={usersOptions || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('labels.attendees')}
                  fullWidth
                  margin="normal"
                  size="small"
                />
              )}
              multiple
              filterSelectedOptions
              getOptionLabel={(option:IOption) => option.name}
              isOptionEqualToValue={(option:IOption, value:IOption) => option.id === value.id}
            />
          )}
        />

        {/* Case */}
        <Controller
          control={control} name="caseId"
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              value={value}
              onChange={(_:any, nextValue:any) => onChange(nextValue)}
              options={casesOptions || []}
              onClose={() => debounceCases('')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('labels.case')}
                  fullWidth
                  margin="normal"
                  size="small"
                  onChange={handleChangeCaseInput}
                />
              )}
              getOptionLabel={(option:IOption) => option.name}
              isOptionEqualToValue={(option:IOption, value:IOption) => option.id === value.id}
            />
          )}
        />

        <Divider sx={{ mt: 2, mb: 1 }} />

        {/* Reminders */}
        {fields.map((item, index) => (
          <Box key={item.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Controller
              control={control} name={`reminders.${index}.durationValue`}
              rules={{ required: isRequired, min: isMin(0) }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  size="small"
                  error={Boolean(errors.reminders?.[index]?.durationValue)}
                  helperText={(errors.reminders?.[index]?.durationValue as any)?.message || ''}
                  required
                  sx={{ maxWidth: 120 }}
                  slotProps={{
                    input: {
                      inputProps: {
                        min: 0
                      }
                    }
                  }}
                />
              )}
            />
            <Controller
              control={control} name={`reminders.${index}.durationUnit`}
              rules={{ required: isRequired }}
              render={({ field }) => (
                <Select
                  {...field}
                  size="small"
                  options={reminderUnitOptions}
                  required
                  error={Boolean(errors.reminders?.[index]?.durationUnit)}
                  helperText={(errors.reminders?.[index]?.durationUnit as any)?.message || ''}
                  sx={{ maxWidth: 160 }}
                />
              )}
            />
            {watch(`reminders.${index}.durationUnit`) === ReminderUnitTypes.Day ? (
              <Fragment>
                <Typography variant="body2" sx={{ minWidth: 'max-content', mt: '27px' }}>
                  {t('labels.beforeAt')}
                </Typography>
                <Controller
                  control={control} name={`reminders.${index}.time`}
                  rules={{
                    required: isRequired,
                    validate: validateEpisodeTime
                  }}
                  render={({ field }) => (
                    <DesktopTimePicker
                      {...field}
                      ampm={is12HourFormat}
                      slotProps={{
                        textField: {
                          size: 'small',
                          required: true,
                          error: Boolean(errors.reminders?.[index]?.time),
                          helperText:
                            (errors.reminders?.[index]?.time as any)?.message || '',
                          sx: {
                            maxWidth: is12HourFormat ? 140 : 110,
                          }
                        }
                      }}
                    />
                  )}
                />
              </Fragment>
            ) : null}
            <Tooltip title={t('buttons.remove')}>
              <IconButton
                name={`remove reminder ${index}`}
                onClick={() => remove(index)}
                sx={{ mt: 4 }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        {fields.length < 5 ? (
          <Button
            name="add reminder"
            onClick={addReminder}
          >
            {t('pages.companyCalendar.addReminderButton')}
          </Button>
          ) : null}
      </form>
    </Dialog>
  )
}

export default EventsFormDialog;
